import React, { useState, useEffect } from "react";
import MainCard from '@/components/MainCard';
import { Table, Button } from "antd";
import { PlayCircle } from "lucide-react";
import {
  bp_get_batch_process_active,
  bp_get_batch_process_schedule,
} from '@/utils/api';
import { useNavigate } from "react-router-dom";
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import ReusableSearchInput from '@/components/ReusableSearch';
import { defaultColors } from '@/themes/antd-theme';

const Batch = () => {
  const [user_access_data, set_user_access_data] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    try {
      const data = localStorage.getItem("user_access_data");
      if (data && data !== "undefined" && data !== "null") {
        set_user_access_data(JSON.parse(data));
      }
    } catch (error) {
      console.error("Error parsing user_access_data from localStorage:", error);
      localStorage.removeItem("user_access_data");
    }
  }, []);

  const navigate = useNavigate();
  const [operation_data, set_operation_data] = useState([]);

  const get_data = async () => {
    try {
      await bp_get_batch_process_active(success_bp_get_batch_process_active);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const success_bp_get_batch_process_active = (res) => {
    set_operation_data(res?.data?.data);
    console.log("operation_data", res?.data?.data);
  };

  useEffect(() => {
    get_data();
  }, []);

  const get_add_routes = () => {
    return user_access_data?.find((item) => item.module_name === "Batch")
      ?.create_access
      ? [
          {
            title: "Ingredients",
            dataIndex: "",
            key: "ingredients",
            align: "center",
            render: (_, record) => {
              return (
                record?.ingredients?.length > 0 &&
                record?.ingredients?.some(
                  (item) => item.ingredient_qty === ""
                ) && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/ingredients", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
          {
            title: "Parameters",
            dataIndex: "",
            key: "parameters",
            align: "center",
            render: (_, record) => {
              return (
                record?.paramaters?.length > 0 && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/parameters", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
          {
            title: "Product",
            dataIndex: "",
            align: "center",
            key: "product",
            render: (_, record) => {
              return (
                record?.products?.length > 0 && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/product", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
          {
            title: "Sampling",
            dataIndex: "",
            align: "center",
            key: "sampling",
            render: (_, record) => {
              return (
                record?.products?.length > 0 && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/sampling", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
        ]
      : [];
  };

  const allColumns = [
    {
      title: "Batch",
      dataIndex: "batch_number",
      key: "batch_number",
      defaultVisible: true,
      sortable: true,
      sortType: "string",
    },
  ];

  const processColumns = [
    { title: "Process", dataIndex: "process_name", key: "process_name" },
  ];

  const operationColumns = [
    {
      title: "Operation",
      dataIndex: "operation_number",
      key: "operation_number",
      render: (_, record) =>
        `${record?.operation_type}-${record?.operation_number}`,
    },
    {
      title: "Instruction",
      dataIndex: "instruction",
      key: "instruction",
    },
    {
      title: "Minimum Duration",
      dataIndex: "min_duration",
      key: "min_duration",
    },
    {
      title: "Maximum Duration",
      dataIndex: "max_duration",
      key: "max_duration",
    },
    ...get_add_routes(),
  ];

  const expandableRowRender = (record) => {
    return (
      <Table
        columns={operationColumns}
        dataSource={record.operations}
        bordered
        size="small"
        pagination={false}
        rowKey={(record) => record.operation_id}
      />
    );
  };

  const expandableProcessRowRender = (record) => {
    return (
      <Table
        columns={processColumns}
        dataSource={record.process}
        bordered
        size="small"
        expandable={{
          expandedRowRender: expandableRowRender,
          rowExpandable: (record) =>
            record.operations && record.operations.length > 0,
        }}
        pagination={false}
        rowKey={(record) => record.process_id}
      />
    );
  };

  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: "batch_list",
    columns: allColumns,
    data: operation_data,
    searchValue,
  });

  return (
    <MainCard>
      <div className="flex justify-between items-center gap-2 mb-4">
        <ReusableSearchInput
          placeholder="Search batches..."
          width={300}
          mt={0}
          mb={0}
          onSearch={(value) => setSearchValue(value)}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <TableColumnConfig menu={columnConfigMenu} className="mb-3" />
      </div>
      <Table
        columns={visibleColumns}
        dataSource={filteredData}
        bordered
        size="small"
        expandable={{
          expandedRowRender: expandableProcessRowRender,
          rowExpandable: (record) =>
            record.process && record.process.length > 0,
        }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        rowKey={(record) => record.batch_id}
      />
    </MainCard>
  );
};

export default Batch;
